import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserPayload } from '@ric/interfaces/schemas';
import { ErrorService } from '@core/http/error.service';
import { handleError } from '@core/http/resource.utils';
import { ScdUser } from '@core/users/models/scd-user.model';
import { ScdUserLookup } from '@core/users/models/scd-user-lookup.model';
import { User } from '@core/users/models/user.model';
import { environment } from '@env/environment';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly httpClient = inject(HttpClient);
    private readonly errorService = inject(ErrorService);
    private readonly resourceName = 'user';
    private readonly baseUrl = `${environment.api.backend}/${this.resourceName}`;

    /**
     * Deliberately without the shared error handling: an unknown user answers with
     * 404, which simply means the signed in person has no access.
     */
    public getUser(gid: string): Observable<User> {
        return this.httpClient.get<User>(`${this.baseUrl}/${gid}`);
    }

    public getUsers(): Observable<User[]> {
        return this.httpClient
            .get<User[]>(this.baseUrl)
            .pipe(catchError(handleError<User[]>('GET users', this.errorService, this.resourceName)));
    }

    /**
     * Looks users up in the corporate directory by GID, mail and/or name.
     * Returns an empty list when the directory does not know the person.
     */
    public getScdUsers(lookup: ScdUserLookup): Observable<ScdUser[]> {
        let params = new HttpParams();
        if (lookup.gid) {
            params = params.set('gid', lookup.gid);
        }
        if (lookup.email) {
            params = params.set('mail', lookup.email);
        }
        if (lookup.firstName) {
            params = params.set('firstName', lookup.firstName);
        }
        if (lookup.surName) {
            params = params.set('surName', lookup.surName);
        }

        return this.httpClient
            .get<ScdUser[]>(`${this.baseUrl}/scd`, { params })
            .pipe(catchError(handleError<ScdUser[]>('GET SCD user', this.errorService, this.resourceName)));
    }

    /**
     * Deliberately without the shared error handling: the caller has to tell a
     * duplicate GID apart from a real failure and raises its own message.
     */
    public createUser(user: UserPayload): Observable<void> {
        return this.httpClient.post<void>(this.baseUrl, user);
    }

    public updateUser(user: UserPayload): Observable<void> {
        return this.httpClient
            .put<void>(this.baseUrl, user)
            .pipe(catchError(handleError<void>('PUT user', this.errorService, this.resourceName)));
    }

    /**
     * Archives a user by replacing their roles with `[ARCHIVED]` server-side.
     * Preferred over deleting because it preserves the record so existing
     * references (e.g. request assignments) remain intact, while preventing
     * the archived user from accessing the app.
     */
    public archiveUser(gid: string): Observable<void> {
        return this.httpClient
            .put<void>(`${this.baseUrl}/${gid}/archive`, {})
            .pipe(catchError(handleError<void>('PUT archive user', this.errorService, this.resourceName)));
    }
}
